import React, { useState, useEffect } from 'react';
import { DateTime } from 'luxon';
import Icon from '@mdi/react';
import { mdiClose, mdiAirplaneTakeoff, mdiCalendarExportOutline } from '@mdi/js';
import { toast } from 'react-toastify';
import { availabilityApi } from '../api/availability';
import CustomFormInput from './forms/CustomFormInput';
import WeeklyDayRows, { makeDay, dayConfigToSlots } from './WeeklyDayRows';
import './WeeklyScheduleModal.css'; // shared day-rows / modal styles (+ purple variant)

/**
 * TravelAvailabilitySetupModal — bulk travel availability setup, one row per
 * trip date (Google-calendar style, shared WeeklyDayRows editor).
 *
 * Auto-opens once after a travel period is created. Replaces the old
 * three-modal chain (day pills → nested TravelDaySlotCreatorModal → copy
 * modal): every trip date is a directly-editable row — tick it on, set times,
 * add slot groups / breaks inline. A copy icon per configured row copies that
 * day's setup to other dates.
 *
 * Purple travel theme; the trip's own colour appears ONLY in the destination
 * pill (never on titles/icons/accents — matches the other travel modals).
 *
 * Saves one availability_overrides doc per configured date via
 * availabilityApi.createOverride({ date, slots, travelPeriodId }) — a travel
 * day's slots are ordinary overrides tagged with travelPeriodId (see
 * AVAILABILITY-ARCHITECTURE.md). Create-only and stateless: it never loads
 * existing overrides; post-creation edits go through the week grid.
 *
 * @param {boolean} isOpen
 * @param {object} travelPeriod - { id, destination, startDate, endDate, color, timezoneAbbr?, timezoneOffset? }
 * @param {function} onSave - called with true after a successful save
 * @param {function} onClose
 * @param {string} [singleDate] - ISO date: single-day mode (the empty-travel-day
 *        ghost click). Shows just that date's row, pre-enabled, no copy icon.
 */
const TravelAvailabilitySetupModal = ({ isOpen, travelPeriod, onSave, onClose, singleDate = null }) => {
    // One row per trip date — key = ISO date, label = compact "Sat 1 Aug"
    // (fits the shared 600px modal width). Single-day mode narrows to the
    // clicked date only.
    const travelDays = singleDate
        ? [singleDate]
        : travelPeriod
            ? getTravelDays(travelPeriod.startDate, travelPeriod.endDate)
            : [];
    const dayRows = travelDays.map(iso => ({
        key: iso,
        label: DateTime.fromISO(iso).toFormat('EEE d MMM'),
    }));

    // Full-trip setup starts all dates unavailable (the instructor ticks on
    // teaching days); single-day mode pre-enables the clicked date.
    const initialDayConfig = () =>
        Object.fromEntries(travelDays.map(iso => [iso, makeDay(!!singleDate)]));
    const [dayConfig, setDayConfig] = useState(initialDayConfig);

    // Lesson duration (minutes per bookable slot)
    const [duration, setDuration] = useState(60);

    // UI state
    const [isSaving, setIsSaving] = useState(false);
    // Copy-to-days modal — { sourceKey } when open
    const [copyModal, setCopyModal] = useState(null);

    // Reset whenever the modal opens for a (new) period or single date
    useEffect(() => {
        if (isOpen && travelPeriod) {
            const days = singleDate
                ? [singleDate]
                : getTravelDays(travelPeriod.startDate, travelPeriod.endDate);
            setDayConfig(Object.fromEntries(days.map(iso => [iso, makeDay(!!singleDate)])));
            setDuration(60);
            setIsSaving(false);
            setCopyModal(null);
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isOpen, travelPeriod?.id, singleDate]);

    // Configured days + total slots (drives the save button label)
    const configuredDays = travelDays.filter(
        iso => dayConfig[iso]?.enabled && dayConfigToSlots(dayConfig[iso], duration).length > 0
    );
    const totalSlots = configuredDays.reduce(
        (sum, iso) => sum + dayConfigToSlots(dayConfig[iso], duration).length,
        0
    );

    const handleCopyDay = (targetKeys) => {
        const source = dayConfig[copyModal.sourceKey];
        setDayConfig(prev => {
            const next = { ...prev };
            targetKeys.forEach(key => {
                next[key] = {
                    enabled: true,
                    groups: source.groups.map(g => ({ ...g })),
                    breaks: source.breaks.map(b => ({ ...b })),
                };
            });
            return next;
        });
        toast.success(`Copied to ${targetKeys.length} day${targetKeys.length !== 1 ? 's' : ''}`);
        setCopyModal(null);
    };

    const handleSaveAll = async () => {
        if (configuredDays.length === 0) {
            toast.warning('Please configure availability for at least one day');
            return;
        }
        setIsSaving(true);
        try {
            // Booked times on these days stay booked: leave them out of the new open
            // availability instead of failing the whole day (the backend refuses a slot
            // that overlaps a booking). Bookings the instructor chose to keep at the
            // clash step land here.
            let tripBookings = [];
            try {
                tripBookings = await availabilityApi.getTravelPeriodBookings(travelPeriod.id);
            } catch (err) {
                console.error('Could not load bookings for this trip; saving without trimming:', err);
            }
            const toMin = t => { const [h, m] = String(t).split(':').map(Number); return h * 60 + m; };
            const bookedByDay = {};
            tripBookings.forEach(b => {
                const day = String(b.bookingDate).slice(0, 10);
                if (!b.startTime || !b.endTime) return;
                (bookedByDay[day] = bookedByDay[day] || []).push(b);
            });
            const keptTimes = [];
            let savedSlots = 0;
            const writes = configuredDays.map(iso => {
                const booked = bookedByDay[iso] || [];
                const slots = dayConfigToSlots(dayConfig[iso], duration).filter(slot =>
                    !booked.some(b => toMin(slot.startTime) < toMin(b.endTime) && toMin(slot.endTime) > toMin(b.startTime))
                );
                booked.forEach(b => keptTimes.push(`${DateTime.fromISO(iso).toFormat('EEE d MMM')} at ${b.startTime}`));
                savedSlots += slots.length;
                if (slots.length === 0) return null;
                return availabilityApi.createOverride({
                    date: iso,
                    slots,
                    travelPeriodId: travelPeriod.id,
                    source: 'manual',
                });
            }).filter(Boolean);
            await Promise.all(writes);
            toast.success(`Created ${savedSlots} slots across ${writes.length} day${writes.length !== 1 ? 's' : ''}`);
            if (keptTimes.length > 0) {
                toast.info(`Kept your existing booking${keptTimes.length !== 1 ? 's' : ''} (${keptTimes.join(', ')}). ${keptTimes.length !== 1 ? 'Those times stay' : 'That time stays'} booked, so ${keptTimes.length !== 1 ? "they weren't" : "it wasn't"} added as open availability.`, { autoClose: 8000 });
            }
            onSave(true);
            onClose();
        } catch (error) {
            console.error('Failed to save travel availability:', error);
            // The shared client's wrappers throw a plain Error with the backend message in
            // error.message; error.response is always undefined via the compat client.
            toast.error(error.message || 'Failed to save. Please try again.');
        } finally {
            setIsSaving(false);
        }
    };

    if (!isOpen || !travelPeriod) return null;

    return (
        <div className="weekly-schedule-modal-overlay" onClick={onClose}>
            <div className="weekly-schedule-modal weekly-schedule-modal--travel" onClick={e => e.stopPropagation()}>
                {/* Header */}
                <div className="weekly-schedule-modal-header">
                    <div className="weekly-schedule-modal-header-content">
                        <Icon path={mdiAirplaneTakeoff} size={1.2} color="var(--dl-color-purple-default)" />
                        <h2>Set Travel Availability</h2>
                    </div>
                    <button className="weekly-schedule-modal-close" onClick={onClose}>
                        <Icon path={mdiClose} size={1} />
                    </button>
                </div>

                {/* Travel context strip — destination pill in the trip's own colour
                    (the ONLY place the per-trip colour appears) + timezone */}
                <div className="weekly-context-banner">
                    <div
                        className="weekly-studio-pill"
                        style={{
                            borderColor: `${travelPeriod.color}80`,
                            backgroundColor: `${travelPeriod.color}15`,
                        }}>
                        <Icon path={mdiAirplaneTakeoff} size={0.7} style={{ color: travelPeriod.color }} />
                        <span>{travelPeriod.destination}</span>
                    </div>
                    {travelPeriod.timezoneAbbr && (
                        <div className="weekly-travel-timezone">
                            {travelPeriod.timezoneAbbr} (UTC{travelPeriod.timezoneOffset})
                        </div>
                    )}
                </div>

                {/* Body */}
                <div className="weekly-schedule-modal-body">
                    {/* Lesson Duration — label + pills share one row */}
                    <div className="form-group weekly-lesson-duration">
                        <label className="form-label">Lesson Duration</label>
                        <div className="duration-radio">
                            {[45, 60, 90, 120].map(d => (
                                <CustomFormInput
                                    key={d}
                                    type="radio"
                                    variant="purple"
                                    layout="pill"
                                    name="travelSetupDuration"
                                    value={String(d)}
                                    label={`${d} min`}
                                    checked={duration === d}
                                    onChange={() => setDuration(d)}
                                />
                            ))}
                        </div>
                    </div>

                    {/* Trip availability — one row per date, purple variant, copy icon
                        (copy hidden in single-day mode: nowhere to copy to) */}
                    <div className="form-group weekly-section-divided">
                        <label className="form-label">Availability while travelling</label>
                        <p className="form-hint">
                            {singleDate
                                ? 'Set your hours for this day. Add slot groups or a break as needed.'
                                : "Tick the days you'll teach and set your hours. Use the copy icon to apply one day's setup to other days."}
                        </p>
                        <WeeklyDayRows
                            dayConfig={dayConfig}
                            setDayConfig={setDayConfig}
                            duration={duration}
                            days={dayRows}
                            variant="purple"
                            onCopyDay={singleDate ? undefined : (key) => setCopyModal({ sourceKey: key })}
                        />
                    </div>
                </div>

                {/* Footer */}
                <div className="weekly-schedule-modal-footer">
                    {!singleDate && (
                        <p className="travel-setup-skip-hint">
                            You can also skip and add availability day by day from the week grid.
                        </p>
                    )}
                    <button className="btn-cancel" onClick={onClose} disabled={isSaving}>
                        {singleDate ? 'Cancel' : 'Skip for now'}
                    </button>
                    <button
                        className="btn-primary"
                        onClick={handleSaveAll}
                        disabled={isSaving || configuredDays.length === 0}>
                        {isSaving
                            ? 'Saving...'
                            : `Save ${totalSlots} Slots (${configuredDays.length} day${configuredDays.length !== 1 ? 's' : ''})`}
                    </button>
                </div>
            </div>

            {/* Copy-to-days mini modal */}
            {copyModal && (
                <CopyToDaysModal
                    sourceKey={copyModal.sourceKey}
                    dayRows={dayRows}
                    dayConfig={dayConfig}
                    onApply={handleCopyDay}
                    onCancel={() => setCopyModal(null)}
                />
            )}
        </div>
    );
};

/**
 * CopyToDaysModal — pick which other trip dates receive the source day's setup.
 * Reuses the break-modal shell (purple variant).
 */
const CopyToDaysModal = ({ sourceKey, dayRows, dayConfig, onApply, onCancel }) => {
    const [selected, setSelected] = useState([]);
    const sourceLabel = dayRows.find(d => d.key === sourceKey)?.label || sourceKey;

    const toggle = (key) =>
        setSelected(prev => (prev.includes(key) ? prev.filter(k => k !== key) : [...prev, key]));

    return (
        <div className="weekly-break-modal-overlay" onClick={(e) => { e.stopPropagation(); onCancel(); }}>
            <div className="weekly-break-modal weekly-break-modal--purple" onClick={(e) => e.stopPropagation()}>
                <div className="weekly-break-modal-header">
                    <div className="weekly-break-modal-header-content">
                        <Icon path={mdiCalendarExportOutline} size={0.9} color="var(--dl-color-purple-default)" />
                        <h3>Copy {sourceLabel} to other days</h3>
                    </div>
                    <button className="weekly-break-modal-close" onClick={onCancel}>
                        <Icon path={mdiClose} size={0.9} />
                    </button>
                </div>
                <div className="weekly-break-modal-body">
                    <div className="weekly-copy-target-list">
                        {dayRows
                            .filter(d => d.key !== sourceKey)
                            .map(d => {
                                const alreadyConfigured = dayConfig[d.key]?.enabled;
                                return (
                                    <CustomFormInput
                                        key={d.key}
                                        type="checkbox"
                                        variant="purple"
                                        layout="standard"
                                        label={alreadyConfigured ? `${d.label} (will overwrite)` : d.label}
                                        checked={selected.includes(d.key)}
                                        onChange={() => toggle(d.key)}
                                    />
                                );
                            })}
                    </div>
                </div>
                <div className="weekly-break-modal-footer">
                    <button className="btn-cancel" onClick={onCancel}>Cancel</button>
                    <button
                        className="btn-primary"
                        onClick={() => onApply(selected)}
                        disabled={selected.length === 0}>
                        Copy to {selected.length} day{selected.length !== 1 ? 's' : ''}
                    </button>
                </div>
            </div>
        </div>
    );
};

/** ISO date strings covering the travel period (inclusive). */
const getTravelDays = (startDate, endDate) => {
    const start = DateTime.fromISO(startDate);
    const end = DateTime.fromISO(endDate);
    const days = [];
    let current = start;
    while (current <= end) {
        days.push(current.toISODate());
        current = current.plus({ days: 1 });
    }
    return days;
};

export default TravelAvailabilitySetupModal;
